import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaUsers, FaCar, FaStar, FaLeaf, FaHeart, FaClock, FaShieldAlt, FaHandshake, FaLightbulb, FaArrowRight, FaQuoteLeft, FaQuoteRight } from 'react-icons/fa';

const AboutPage = () => {
    const timeline = [
        {
            year: '2023',
            title: 'The Beginning',
            description: 'Goprezz was founded with a simple mission: to make everyday life easier for busy professionals and families in Hyderabad.',
        },
        {
            year: '2024',
            title: 'Building Trust',
            description: 'Started serving residential communities across Hyderabad with our daily car cleaning and ironing services.',
        },
        {
            year: '2025',
            title: 'Growing Together',
            description: 'Expanded our service offerings and started building a loyal customer base.',
        },
        {
            year: '2026',
            title: 'The Journey Continues',
            description: 'Focused on delivering exceptional quality while maintaining our commitment to customer satisfaction.',
        },
    ];

    const values = [
        {
            icon: FaClock,
            title: 'Time is Precious',
            description: 'We believe your time should be spent on what matters most to you, not on routine chores.',
        },
        {
            icon: FaStar,
            title: 'Quality First',
            description: 'Every service we offer is delivered with the highest standards of quality and professionalism.',
        },
        {
            icon: FaLeaf,
            title: 'Eco-Conscious',
            description: 'We use environmentally friendly products and waterless technology to protect our planet.',
        },
        {
            icon: FaHeart,
            title: 'Customer Care',
            description: 'Your satisfaction is our priority. We go above and beyond to exceed expectations.',
        },
        {
            icon: FaShieldAlt,
            title: 'Trust & Safety',
            description: 'All our staff are background-verified and trained to ensure your peace of mind.',
        },
        {
            icon: FaHandshake,
            title: 'Community Partner',
            description: 'We work closely with communities to provide tailored solutions that benefit everyone.',
        },
    ];

    const team = [
        {
            name: 'Our Leadership',
            description: 'Experienced professionals committed to operational excellence and customer satisfaction.',
        },
        {
            name: 'Service Team',
            description: 'Trained technicians who treat your belongings with care and professionalism.',
        },
        {
            name: 'Support Team',
            description: 'Dedicated customer support ready to assist you with any queries or concerns.',
        },
    ];



    return (
        <div className="min-h-screen pt-20 bg-white">
            {/* Hero Section */}
            <section className="section-padding relative overflow-hidden bg-white">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 bg-primary-200"></div>
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6 bg-primary-50 text-primary-600">
                                About Goprezz
                            </span>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900">
                                Making Daily Life
                                <br />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">Effortlessly Simple</span>
                            </h1>
                            <p className="text-lg leading-relaxed mb-8 text-gray-600">
                                Goprezz was born from a simple observation: busy professionals and families
                                spend too much time on routine tasks that could be handled by trusted experts.
                                We set out to change that.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/services" className="btn-primary">
                                    Explore Services
                                </Link>
                                <Link to="/contact" className="px-8 py-3 rounded-full font-semibold border-2 border-primary-500 text-primary-600 hover:bg-primary-50 transition-all">
                                    Partner With Us
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="relative"
                        >
                            <img
                                src="/images/team.png"
                                alt="Goprezz Team"
                                className="rounded-3xl w-full shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>



            {/* Our Story */}
            <section className="section-padding relative overflow-hidden bg-gray-50">
                <div className="max-w-4xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 bg-primary-50 text-primary-600">
                            Our Story
                        </span>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
                            Why We
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> Started</span>
                        </h2>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="p-8 md:p-12 rounded-3xl relative bg-white border border-gray-100 shadow-xl"
                    >
                        <div className="absolute top-6 left-6 w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center">
                            <FaQuoteLeft className="w-6 h-6 text-primary-600" />
                        </div>
                        <div className="absolute bottom-6 right-6 w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center">
                            <FaQuoteRight className="w-6 h-6 text-primary-600" />
                        </div>

                        <div className="space-y-6 leading-relaxed text-lg text-gray-600">
                            <p>
                                <strong className="text-gray-900">Our day begins on a positive note when our daily routines are well organized.</strong> Wearing properly sourced clothes that feel fresh and clean can influence many essential daily activities. A clean car waiting for you every morning sets the tone for a productive day.
                            </p>
                            <p>
                                However, <strong className="text-gray-900">services in this sector often remain unorganized.</strong> Delays, inconsistent quality, and lack of proper systems disrupt our routines and affect peace of mind. We've all experienced the frustration of waiting for services that never arrive on time.
                            </p>
                            <p>
                                <strong className="text-primary-600">Goprezz was started with a simple idea:</strong> to bring multiple services together on a single platform, so customers can plan their day easily without worrying about these everyday tasks.
                            </p>
                            <p>
                                Our founder experienced these challenges firsthand while managing daily responsibilities alongside a busy career. The idea was born from necessity—creating a service that professionals could rely on without a second thought.
                            </p>
                            <p className="text-xl font-medium text-gray-900">
                                The main vision of Goprezz is to save our customers valuable time and help them keep their daily lives smooth and hassle-free.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Timeline */}
            <section className="section-padding bg-gray-100">
                <div className="max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 bg-white text-primary-600 shadow-sm border border-gray-100">
                            Our Journey
                        </span>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
                            The Goprezz
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> Timeline</span>
                        </h2>
                    </motion.div>

                    <div className="relative">
                        {/* Timeline Line */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full hidden md:block bg-gradient-to-b from-primary-200 to-primary-400"></div>

                        {timeline.map((item, index) => (
                            <motion.div
                                key={item.year}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                                className={`flex items-center gap-8 mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                            >
                                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                    <div className="p-6 rounded-2xl inline-block bg-white shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                                        <span className="font-bold text-lg text-primary-600">{item.year}</span>
                                        <h3 className="text-xl font-semibold mt-2 mb-2 text-gray-900">{item.title}</h3>
                                        <p className="text-gray-600">{item.description}</p>
                                    </div>
                                </div>

                                <div className="w-4 h-4 rounded-full relative z-10 hidden md:block bg-primary-500 border-4 border-white shadow-md"></div>

                                <div className="flex-1 hidden md:block"></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="section-padding bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 bg-primary-50 text-primary-600">
                            What We Believe
                        </span>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
                            Our Core
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> Values</span>
                        </h2>
                        <p className="text-lg md:text-xl text-center max-w-2xl mx-auto mb-12 text-gray-600">
                            These principles guide everything we do at Goprezz
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="p-6 md:p-8 rounded-2xl transition-all duration-300 group hover:shadow-lg bg-gray-50 border border-gray-100 hover:bg-white"
                            >
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform bg-primary-50 text-primary-600">
                                    <value.icon className="w-7 h-7" />
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-gray-900">{value.title}</h3>
                                <p className="text-gray-600">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Promise */}
            <section className="section-padding bg-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10 bg-primary-300"></div>
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6 bg-primary-50 text-primary-600">
                                Our Commitment
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                                Our Promise
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> To You</span>
                            </h2>
                            <p className="mb-8 leading-relaxed text-gray-600">
                                We are committed to delivering services that make everyday life easier,
                                smoother, and stress-free. Here's what you can always expect from Goprezz:
                            </p>

                            <div className="space-y-4">
                                {[
                                    'Punctual, reliable service every single day',
                                    'Trained, professional, and courteous staff',
                                    'Eco-friendly products and practices',
                                    'Transparent pricing with no hidden charges',
                                    'Responsive customer support',
                                    'Satisfaction guaranteed or your money back',
                                ].map((promise, index) => (
                                    <motion.div
                                        key={promise}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-3"
                                    >
                                        <FaCheckCircle className="w-5 h-5 flex-shrink-0 text-primary-500" />
                                        <span className="text-gray-700">{promise}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="grid grid-cols-2 gap-4"
                        >
                            {[
                                { icon: FaCar, value: 'Daily', label: 'Car Cleaning' },
                                { icon: FaClock, value: '24hr', label: 'Ironing Turnaround' },
                                { icon: FaLeaf, value: '100%', label: 'Eco-Friendly' },
                                { icon: FaStar, value: '4.9★', label: 'Rating' },
                            ].map((item, index) => (
                                <motion.div
                                    key={item.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="p-6 text-center rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <item.icon className="w-8 h-8 mx-auto mb-3 text-primary-500" />
                                    <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                                    <div className="text-sm text-gray-500">{item.label}</div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-800">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl md:text-4xl font-bold mb-6 text-white"
                    >
                        Ready to Experience the Goprezz Difference?
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-lg mb-8 text-white/90"
                    >
                        Join hundreds of happy customers who have simplified their daily routines with our services.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <a
                            href="https://wa.me/919059651065"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 bg-white text-primary-600 hover:bg-white/90"
                        >
                            Start Your Free Trial
                        </a>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 bg-white text-primary-600 hover:bg-white/90 shadow-lg"
                        >
                            Contact Us
                            <FaArrowRight className="ml-2" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
